/* eslint-disable react-hooks/exhaustive-deps */
import { useLocation } from "react-router-dom";
import React, { useEffect, useState } from "react";
import axios, { AxiosResponse } from "axios";
import MainLayout from "../../shared/components/MainLayout";
import Loader from "../../shared/components/loader/Loader";
import { FaRegMoneyBillAlt, FaRegCalendarAlt, FaCheckCircle, FaTimesCircle, FaGift } from "react-icons/fa";

interface PaymentMethod {
    cardType: string;
    last4: string;
    expirationMonth: string;
    expirationYear: string;
}

interface Payment {
    paymentId: string;
    amount: string;
    status: string;
    createdAt: string;
}

interface Subscription {
    id: string;
    plan: string;
    status: string;
    amount: string;
    product: string;
}

interface ClientDetails {
    name: string;
    email: string;
    status: string;
    createdAt: string;
    subscriptions: Subscription[];
    paymentMethods: PaymentMethod[];
    payments: Payment[];
}



const ClientDetail: React.FC = () => {
    const location = useLocation();
    const { clientId } = location.state || {}; // Ottieni clientId dalla location.state

    const [clientDetails, setClientDetails] = useState<ClientDetails | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Funzione per ottenere i dettagli del cliente
    const getClientDetails = async () => {
        const baseUrl = process.env.REACT_APP_API_BASE_URL;
        const url = `${baseUrl}/nexum-saas/monitorPurchase`; // Endpoint API per i dettagli cliente
        const token = localStorage.getItem("token");

        try {
            const headers = token
                ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
                : { "Content-Type": "application/json" };

            const body = {
                path: "clientDetails", // Corpo della richiesta POST
                customerId: clientId,
            };

            // Chiamata alla API per ottenere i dettagli del cliente
            const response: AxiosResponse<ClientDetails> = await axios.post(url, body, { headers });

            // Verifica se i dati ricevuti sono corretti
            if (response.data) {
                setClientDetails(response.data); // Imposta i dettagli cliente
            } else {
                setError("Dati non validi ricevuti dal server.");
            }
        } catch (err) {
            //console.error("Errore durante il recupero dei dati:", err);
            throw new Error("Failed to retrieve data");
        } finally {
            setLoading(false);
        }
    };

    // Carica i dettagli del cliente quando cambia il clientId
    useEffect(() => {
        if (clientId) {
            getClientDetails();
        } else {
            setError("Client ID non trovato.");
        }
    }, [clientId]);

    // Visualizzazione durante il caricamento
    if (loading) {
        return <Loader />;
    }

    // Visualizzazione degli errori
    if (error) {
        return (
            <MainLayout>
                <div className="text-center text-red-500 font-semibold">
                    {error}
                </div>
            </MainLayout>
        );
    }
    // Se non ci sono errori, mostra i dettagli del cliente
    return (
        <MainLayout>
            <div className="p-6 bg-gray-100 min-h-screen">
                <div className="max-w-6xl mx-auto bg-white shadow-md rounded-lg p-6">
                    <h1 className="text-3xl font-semibold text-gray-800 mb-6">Client Details</h1>

                    {/* Customer Information */}
                    <section className="mb-6">
                        <h2 className="text-xl font-semibold text-gray-700 mb-4">Customer Information</h2>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="p-4 bg-gray-50 border rounded-lg">
                                <p className="text-sm text-gray-500">Name</p>
                                <p className="font-medium text-gray-800">{clientDetails?.name || "N/A"}</p>
                            </div>
                            <div className="p-4 bg-gray-50 border rounded-lg">
                                <p className="text-sm text-gray-500">Email</p>
                                <p className="font-medium text-gray-800">{clientDetails?.email || "N/A"}</p>
                            </div>
                            <div className="p-4 bg-gray-50 border rounded-lg">
                                <p className="text-sm text-gray-500">Status</p>
                                <p className="font-medium text-gray-800">{clientDetails?.status || "Active"}</p>
                            </div>
                            <div className="p-4 bg-gray-50 border rounded-lg">
                                <p className="text-sm text-gray-500">Created At</p>
                                <p className="font-medium text-gray-800">{clientDetails?.createdAt || "N/A"}</p>
                            </div>
                        </div>
                    </section>

                    {/* Subscriptions */}
                    <section className="mb-6">
                        <h2 className="text-xl font-semibold text-gray-700 mb-4">Subscriptions</h2>
                        {clientDetails?.subscriptions.length === 0 ? (
                            <div className="text-gray-500">No active subscriptions</div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="table-auto w-full text-left border-collapse">
                                    <thead>
                                        <tr className="bg-gray-100">
                                            <th className="px-4 py-2 text-left text-gray-600">Product</th>
                                            <th className="px-4 py-2 text-left text-gray-600">Plan</th>
                                            <th className="px-4 py-2 text-left text-gray-600">Status</th>
                                            <th className="px-4 py-2 text-left text-gray-600">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {clientDetails?.subscriptions.map((subscription) => (
                                            <tr
                                                key={subscription.id}
                                                className="border-t hover:bg-gray-50"
                                            >
                                                {/* Product Column */}
                                                <td className="px-4 py-2 text-left">
                                                    <div className="flex items-center space-x-2">
                                                        <FaGift className="text-purple-500" /> {/* Usa l'icona per il prodotto */}
                                                        <span>{subscription.product}</span>
                                                    </div>
                                                </td>
                                                {/* Plan Column */}
                                                <td className="px-4 py-2">
                                                    <div className="flex items-center space-x-2">
                                                        <FaRegCalendarAlt className="text-blue-500 " />
                                                        <span>{Number(subscription.amount) < 100 ? "Monthly" : "Annually"}</span>
                                                    </div>
                                                </td>

                                                {/* Status Column */}
                                                <td className="px-4 py-2">
                                                    <div className="flex items-center space-x-2">
                                                        {subscription.status === "active" ? (
                                                            <FaCheckCircle className="text-green-500" />
                                                        ) : (
                                                            <FaTimesCircle className="text-red-500" />
                                                        )}
                                                        <span>{subscription.status}</span>
                                                    </div>
                                                </td>

                                                {/* Amount Column */}
                                                <td className="px-4 py-2">
                                                    <div className="flex items-center space-x-2">
                                                        <FaRegMoneyBillAlt className="text-yellow-500" />
                                                        <span>€{subscription.amount}</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>

                    {/* Payment Methods */}
                    <section className="mb-6">
                        <h2 className="text-xl font-semibold text-gray-700 mb-4">Payment Methods</h2>
                        {clientDetails?.paymentMethods.length === 0 ? (
                            <div className="text-gray-500">No payment methods available</div>
                        ) : (
                            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {clientDetails?.paymentMethods.map((method, index) => (
                                    <li
                                        key={index}
                                        className="p-4 bg-gray-50 border rounded-lg flex items-center"
                                    >
                                        <FaRegMoneyBillAlt className="text-green-500 mr-4" />
                                        <div>
                                            <p className="text-sm text-gray-500">
                                                {method.cardType.toUpperCase()} ending in {method.last4}
                                            </p>
                                            <p className="text-sm text-gray-500">
                                                Expires {method.expirationMonth}/{method.expirationYear}
                                            </p>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>

                    {/* Payment History */}
                    <section>
                        <h2 className="text-xl font-semibold text-gray-700 mb-4">Payment History</h2>
                        {clientDetails?.payments.length === 0 ? (
                            <div className="text-gray-500">No payments made</div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="table-auto w-full text-left border-collapse">
                                    <thead>
                                        <tr className="bg-gray-100">
                                            <th className="px-4 py-2 text-left text-gray-600">Payment ID</th>
                                            <th className="px-4 py-2 text-left text-gray-600">Amount</th>
                                            <th className="px-4 py-2 text-left text-gray-600">Status</th>
                                            <th className="px-4 py-2 text-left text-gray-600">Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {clientDetails?.payments.map((payment) => (
                                            <tr
                                                key={payment.paymentId}
                                                className="border-t hover:bg-gray-50"
                                            >
                                                <td className="px-4 py-2">{payment.paymentId}</td>
                                                <td className="px-4 py-2">${payment.amount}</td>
                                                <td className="px-4 py-2">{payment.status}</td>
                                                <td className="px-4 py-2">{payment.createdAt}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>
                </div>
            </div>
        </MainLayout>
    );
};

export default ClientDetail;